import { Fragment, useMemo } from "react";
import { ChevronDown, ChevronRight, Download, Loader2 } from "lucide-react";
import { useDashboardChart } from "@/hooks/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import { downloadCsv, toCsv } from "@/lib/utils/csv";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";
import { ReferralDemographics, AGE_GROUPS, type DemographicRow } from "./referral-demographics";
import { ReferralVolume } from "./referral-volume";
import { GRANULARITIES, MAX_REFERRAL_POINTS, referralPeriods, effectiveGranularity, periodLabel } from "./referral-periods";

const DASHBOARD_ID = "referrals";
// Norwegian Excel reads comma-separated CSV as a single column.
const CSV_DELIMITER = ";";

type OverTimeRow = { period: string; referrals: number };

type VervetCodeRow = { period: string; customers: number };

type ReferredCustomer = {
  auth_user_id: string;
  name: string | null;
  email: string | null;
  started: string;
};

type ReferrerRow = {
  auth_user_id: string;
  name: string | null;
  email: string | null;
  referrals: number;
  referred: ReferredCustomer[];
};

const REFERRERS_COLUMNS: DashboardTableColumn<ReferrerRow>[] = [
  { id: "name", label: "Navn", value: (r) => r.name },
  { id: "email", label: "E-post", value: (r) => r.email },
  { id: "referrals", label: "Vervede", value: (r) => r.referrals, numeric: true, align: "right" },
];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split("T")[0]!;
}

export function ReferralsDashboard() {
  const [state, update] = useDashboardUrlState({
    from: isoDaysAgo(365),
    to: isoDaysAgo(0),
    granularity: "month",
    expanded: "",
    ageGroup: "",
    gender: "",
    tier: "",
    filterRole: "referred",
    referralsMode: "chart",
    "vervet-codeMode": "table",
    referrerDimension: "age",
    referrerView: "absolute",
    referrerMode: "chart",
    referredDimension: "age",
    referredView: "absolute",
    referredMode: "chart",
  });
  const { from, to, expanded, ageGroup, gender, tier, filterRole } = state;
  const granularity = effectiveGranularity(from, to, state.granularity);

  const params = useMemo(() => ({ from, to, filterRole, ageGroup: ageGroup || undefined, gender: gender || undefined, tier: tier || undefined }), [from, to, filterRole, ageGroup, gender, tier]);
  const optionParams = useMemo(() => ({ from, to, filterRole }), [from, to, filterRole]);
  const options = useDashboardChart<typeof optionParams, { tier: string }>(DASHBOARD_ID, "filter-options", optionParams);
  const tiers = [...new Set([...(options.data ?? []).map((r) => r.tier), ...(tier ? [tier] : [])])].sort((a, b) => a.localeCompare(b, "nb"));
  // Separate object so toggling granularity doesn't refetch the referrers.
  const overTimeParams = useMemo(
    () => ({ ...params, granularity }),
    [params, granularity],
  );

  const overTime = useDashboardChart<typeof overTimeParams, OverTimeRow>(
    DASHBOARD_ID,
    "referrals-over-time",
    overTimeParams,
  );
  const referrers = useDashboardChart<typeof params, ReferrerRow>(
    DASHBOARD_ID,
    "referrers",
    params,
  );
  const vervetCode = useDashboardChart<typeof overTimeParams, VervetCodeRow>(
    DASHBOARD_ID,
    "vervet-code-over-time",
    overTimeParams,
  );
  const demographics = useDashboardChart<typeof overTimeParams, DemographicRow>(
    DASHBOARD_ID, "demographics-over-time", overTimeParams,
  );

  const referrersTable = useDashboardTable(
    "referrers",
    REFERRERS_COLUMNS,
    referrers.data,
  );

  // One line per referred customer, so the file holds what the rows expand to.
  const exportReferrers = () => {
    const csv = toCsv(
      ["Verver", "Verver e-post", "Antall vervede", "Vervet kunde", "Vervet e-post", "Kunde fra"],
      referrersTable.viewRows.flatMap((r) =>
        r.referred.map((c) => [r.name, r.email, r.referrals, c.name, c.email, c.started]),
      ),
      CSV_DELIMITER,
    );
    downloadCsv(csv, `ververe-${from}-${to}.csv`, { bom: true });
  };

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Fra</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Til</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
          <div className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Tidsinndeling</span>
            <div className="flex gap-2">
              {GRANULARITIES.map((g) => (
                <Button key={g.value} variant={granularity === g.value ? "default" : "outline"} size="sm"
                  disabled={referralPeriods(from, to, g.value).length > MAX_REFERRAL_POINTS}
                  title={referralPeriods(from, to, g.value).length > MAX_REFERRAL_POINTS ? "Velg en kortere periode for denne tidsinndelingen." : undefined}
                  onClick={() => update({ granularity: g.value })}>
                  {g.label}
                </Button>
              ))}
            </div>
          </div>
          <label className="space-y-1"><span className="text-xs text-admin-text-subtle">Filtrer på</span>
            <Select value={filterRole} onChange={(e) => update({ filterRole: e.target.value, expanded: "" })}>
              <option value="referred">Vervet kunde</option><option value="referrer">Verver</option>
            </Select>
          </label>
          <label className="space-y-1"><span className="text-xs text-admin-text-subtle">Aldersgruppe</span>
            <Select value={ageGroup} onChange={(e) => update({ ageGroup: e.target.value, expanded: "" })}>
              <option value="">Alle aldre</option>{AGE_GROUPS.map((age) => <option key={age} value={age}>{age}</option>)}
            </Select>
          </label>
          <label className="space-y-1"><span className="text-xs text-admin-text-subtle">Abonnement</span>
            <Select value={tier} onChange={(e) => update({ tier: e.target.value, expanded: "" })} className="max-w-72">
              <option value="">Alle abonnementer</option>{tiers.map((value) => <option key={value} value={value}>{value}</option>)}
            </Select>
          </label>
          <label className="space-y-1"><span className="text-xs text-admin-text-subtle">Kjønn</span>
            <Select value={gender} onChange={(e) => update({ gender: e.target.value, expanded: "" })}>
              <option value="">Alle kjønn</option><option value="female">Kvinne</option><option value="male">Mann</option><option value="unknown">Ukjent</option>
            </Select>
          </label>
          <Button variant="outline" size="sm" onClick={() => update({ ageGroup: "", gender: "", tier: "", filterRole: "referred", expanded: "" })}>Nullstill kundefiltre</Button>
        </div>
        <p className="text-xs text-admin-text-subtle">Uker starter mandag. Lange grafer kan rulles sidelengs. Tidsinndelinger over {MAX_REFERRAL_POINTS} punkter er deaktivert for valgt periode.{granularity !== state.granularity ? ` Viser ${periodLabel(granularity)} for denne perioden.` : ""}</p>
        <p className="text-xs text-admin-text-subtle">Alle grafer, tabeller og CSV-er viser samme utvalg av vervinger. Kundefiltrene gjelder {filterRole === "referrer" ? "ververen" : "den vervede kunden"} ved vervingen; begge sider av disse vervingene vises nedenfor.</p>
        {options.isError && <ErrorBlock message="Kunne ikke hente abonnementstyper. Prøv igjen ved å laste siden på nytt." />}
      </FiltersCard>

      <ReferralVolume id="referrals" title="Vervinger over tid" rows={(overTime.data ?? []).map((r) => ({ period: r.period, count: r.referrals }))}
        {...{ from, to, granularity }} isLoading={overTime.isLoading} error={overTime.isError ? overTime.error?.message || "Kunne ikke hente vervinger." : undefined} />

      {demographics.isLoading ? <ChartSkeleton /> : demographics.isError ? <ErrorBlock message={demographics.error?.message} /> : (
        <ReferralDemographics rows={demographics.data ?? []} {...{ from, to, granularity }} />
      )}

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Ververe</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={exportReferrers}
            disabled={!referrers.data || referrers.data.length === 0}
          >
            <Download className="h-3 w-3" />
            Eksporter CSV
          </Button>
        </CardHeader>
        <CardContent>
          {referrers.isLoading ? (
            <ChartSkeleton />
          ) : referrers.isError ? (
            <ErrorBlock message={referrers.error?.message} />
          ) : !referrers.data || referrers.data.length === 0 ? (
            <EmptyBlock>Ingen vervinger i perioden.</EmptyBlock>
          ) : (
            <DashboardTable
              table={referrersTable}
              columns={REFERRERS_COLUMNS}
              renderRow={(row) => {
                const open = expanded === row.auth_user_id;
                const Chevron = open ? ChevronDown : ChevronRight;
                return (
                  <Fragment key={row.auth_user_id}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() =>
                        update({ expanded: open ? "" : row.auth_user_id })
                      }
                    >
                      <TableCell className="font-medium text-admin-text-primary">
                        <span className="inline-flex items-center gap-1.5">
                          <Chevron className="h-3.5 w-3.5 text-admin-text-subtle" />
                          {row.name ?? "—"}
                        </span>
                      </TableCell>
                      <TableCell className="text-admin-text-secondary">
                        {row.email ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.referrals}
                      </TableCell>
                    </TableRow>
                    {open &&
                      row.referred.map((c) => (
                        <TableRow key={c.auth_user_id} className="bg-admin-card-hover">
                          <TableCell className="pl-10 text-admin-text-secondary">
                            {c.name ?? "—"}
                          </TableCell>
                          <TableCell className="text-admin-text-subtle">
                            {c.email ?? "—"}
                          </TableCell>
                          <TableCell className="text-right text-admin-text-subtle tabular-nums">
                            Kunde fra {c.started}
                          </TableCell>
                        </TableRow>
                      ))}
                  </Fragment>
                );
              }}
            />
          )}
        </CardContent>
      </Card>

      <ReferralVolume id="vervet-code" title="VERVET-kode uten kjent verver" rows={(vervetCode.data ?? []).map((r) => ({ period: r.period, count: r.customers }))}
        {...{ from, to, granularity }} isLoading={vervetCode.isLoading} error={vervetCode.isError ? vervetCode.error?.message || "Kunne ikke hente kodebruk." : undefined}
        note={filterRole === "referrer" && Boolean(ageGroup || gender || tier) ? "Ingen kjent verver: disse kundene kan ikke matches mot de valgte ververfiltrene." : "Samme kundefiltre gjelder også her. Kunder uten kjent verver vises separat fra koblede vervinger."} />
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">
      {children}
    </p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
